"use client";
import { create } from "zustand";
import {
  FORM_INFO,
  STEP_B2C_TYPE,
  TOTAL_B2B_ASSESSMENT_STEPS,
  TOTAL_B2C_ASSESSMENT_STEPS,
} from "./constants";
import { persist, createJSONStorage } from "zustand/middleware";
import { b2cTypeValuesToIndex } from "@/lib/get-page-info";
import range from "lodash.range";
import { sharedFormSchema } from "@/components/forms/shared-form";

const INITIAL_STEP_1_VALUES = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  zipCode: "",
};

export type ServiceCategories = "services-for-families" | "medical-staffing";

export interface Step1Values {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  zipCode: string;
}

export type Steps = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export type B2cType =
  | ["Medical Care"]
  | ["Non-Medical Care"]
  | ["Medical Care", "Non-Medical Care"];

// Table of contents
interface AssessmentStore {
  step: Steps;
  totalSteps: number;
  nextStep: () => void;
  prevStep: () => void;

  // Step 1
  serviceCategory: ServiceCategories;
  step1Valid: boolean;
  step1Values: Step1Values;
  submittedStep1Values: Step1Values;
  makeStep1Valid: () => void;
  makeStep1Invalid: () => void;
  checkIfStep1IsValid: () => void;
  changeStep1Values: (v: Step1Values) => void;
  setServiceCategory: (s: ServiceCategories) => void;
  resetToStep1: (hard?: boolean) => void;
  setServiceCategoryAndSubmittedValues: (s: ServiceCategories) => void;

  // role-form
  role: string;
  organization: string;
  changeRole: (s: string) => void;
  changeOrganization: (s: string) => void;
  makeRoleStepValid: () => void;
  makeRoleStepInvalid: () => void;

  // Staffing numbers
  cnaNum: number;
  changeCnaNum: (e: number) => void;
  medTechNum: number;
  changeMedTechNum: (e: number) => void;
  lvnNum: number;
  changeLvnNum: (e: number) => void;
  rnNum: number;
  changeRnNum: (e: number) => void;
  ptNum: number;
  changePtNum: (e: number) => void;
  otNum: number;
  changeOtNum: (e: number) => void;
  officeStaffNum: number;
  changeOfficeStaffNum: (e: number) => void;
  pcNum: number;
  changePcNum: (e: number) => void;
  validateStaffNum: () => void;

  // Step 2-9 values
  stepValid: { [key: number]: boolean };
  stepValues: { [key: number]: string[] };
  toggleSelectAllOnStep: (s: number) => void;
  addOtherValueOnStep: (s: number, v: string) => void;
  toggleCheckboxOnStep: (s: number, v: string) => void;
  toggleRadioOnStep: (s: number, v: string) => void;
  purgeNextSteps: (s: number) => void;

  // Scheduling
  isScheduling: boolean;
  setIsScheduling: (s: boolean) => void;
}

// This store contains all states related to the assessment form
export const useAssessmentStore = create<AssessmentStore>()(
  persist(
    (set, get) => ({
      step: 1,
      totalSteps: TOTAL_B2C_ASSESSMENT_STEPS,
      nextStep: () => {
        set((state) => {
          if (state.step === 1) {
            state.submittedStep1Values = { ...state.step1Values };
          }
          return {
            step: (state.step + 1) as Steps,
          };
        });
      },
      prevStep: () =>
        set((state) => ({
          step: (state.step - 1) as Steps,
        })),

      // Step 1
      step1Valid: false,
      serviceCategory: "services-for-families",
      step1Values: INITIAL_STEP_1_VALUES,
      submittedStep1Values: INITIAL_STEP_1_VALUES,
      makeStep1Valid: () =>
        set(() => ({
          step1Valid: true,
        })),
      makeStep1Invalid: () =>
        set(() => ({
          step1Valid: false,
        })),
      checkIfStep1IsValid: () =>
        set((state) => {
          const isValid = sharedFormSchema.safeParse(
            state.submittedStep1Values
          );

          return {
            step1Valid: isValid.success,
          };
        }),
      changeStep1Values: (value) =>
        set(() => ({
          step1Values: value,
        })),
      setServiceCategory: (s) =>
        set(() => {
          // Purge values on next steps
          get().purgeNextSteps(1);

          return {
            serviceCategory: s,
            totalSteps:
              s === "services-for-families"
                ? TOTAL_B2C_ASSESSMENT_STEPS
                : TOTAL_B2B_ASSESSMENT_STEPS,
          };
        }),
      resetToStep1: (hard = false) =>
        set((state) => {
          // Purge values on next steps
          state.purgeNextSteps(1);

          return {
            step: 1,
            step1Valid: false,
            isScheduling: false,
            submittedStep1Values: hard
              ? INITIAL_STEP_1_VALUES
              : state.submittedStep1Values,
          };
        }),
      setServiceCategoryAndSubmittedValues: (s) =>
        set((state) => ({
          submittedStep1Values: {
            ...state.submittedStep1Values,
            serviceCategory: s,
          },
        })),

      // Role form
      role: "",
      organization: "",
      changeRole: (value) =>
        set(() => ({
          role: value,
        })),
      changeOrganization: (value) =>
        set(() => ({
          organization: value,
        })),
      makeRoleStepValid: () =>
        set((state) => ({
          stepValid: {
            ...state.stepValid,
            2: true,
          },
        })),
      makeRoleStepInvalid: () =>
        set((state) => ({
          stepValid: {
            ...state.stepValid,
            2: false,
          },
        })),

      // Staffing number
      cnaNum: 0,
      changeCnaNum: (value) => {
        set(() => ({
          cnaNum: value,
        }));
        get().validateStaffNum();
      },
      medTechNum: 0,
      changeMedTechNum: (value) => {
        set(() => ({
          medTechNum: value,
        }));
        get().validateStaffNum();
      },
      lvnNum: 0,
      changeLvnNum: (value) => {
        set(() => ({
          lvnNum: value,
        }));
        get().validateStaffNum();
      },
      rnNum: 0,
      changeRnNum: (value) => {
        set(() => ({
          rnNum: value,
        }));
        get().validateStaffNum();
      },
      ptNum: 0,
      changePtNum: (value) => {
        set(() => ({
          ptNum: value,
        }));
        get().validateStaffNum();
      },
      otNum: 0,
      changeOtNum: (value) => {
        set(() => ({
          otNum: value,
        }));
        get().validateStaffNum();
      },
      officeStaffNum: 0,
      changeOfficeStaffNum: (value) => {
        set(() => ({
          officeStaffNum: value,
        }));
        get().validateStaffNum();
      },
      pcNum: 0,
      changePcNum: (value) => {
        set(() => ({
          pcNum: value,
        }));
        get().validateStaffNum();
      },
      validateStaffNum: () =>
        set((state) => {
          const staffRequired = state.stepValues[5];

          // If this validator is called and no staff is required,
          // Step will be invalidated. This case should never happen.
          if (staffRequired.length <= 0)
            return {
              stepValid: {
                ...state.stepValid,
                6: false,
              },
            };

          // Checks that every required staff number is atleast 1
          const valid = staffRequired
            .filter((e) => e !== "select-all")
            .every((staff) => {
              if (
                staff === "Certified Nursing Assistant (CNA)" &&
                state.cnaNum > 0
              )
                return true;
              if (staff === "Medication Technician" && state.medTechNum > 0)
                return true;
              if (
                staff === "Licensed Vocational Nurse (LVN)" &&
                state.lvnNum > 0
              )
                return true;
              if (staff === "Registered Nurse (RN)" && state.rnNum > 0)
                return true;
              if (staff === "Physical Therapist" && state.ptNum > 0)
                return true;
              if (staff === "Occupational Therapist" && state.otNum > 0)
                return true;
              if (staff === "Front Office Staff" && state.officeStaffNum > 0)
                return true;
              if (staff === "Personal Concierge" && state.pcNum > 0)
                return true;

              // This would only be reached on extreme cases that should never happen
              // and let's invalidate the step to be
              return false;
            });

          return {
            stepValid: {
              ...state.stepValid,
              6: valid,
            },
          };
        }),

      // Step 2 to 9 values
      stepValid: {
        2: false,
        3: false,
        4: false,
        5: false,
        6: false,
        7: false,
        8: false,
        9: false,
      },
      stepValues: { 2: [], 3: [], 4: [], 5: [], 6: [], 7: [], 8: [], 9: [] },
      // This is used by the "select all" option
      toggleSelectAllOnStep: (step) => {
        set((state) => {
          let optionsOnStep: string[] = [];
          const serviceCategory = state.serviceCategory;

          const isToggledOn = !state.stepValues[step].includes("select-all");

          if (isToggledOn) {
            // Get all values from the options related to that step
            if (serviceCategory === "services-for-families") {
              const b2cIndex = b2cTypeValuesToIndex(
                state.stepValues[STEP_B2C_TYPE] as B2cType
              );
              optionsOnStep = FORM_INFO[step as Steps][serviceCategory][
                b2cIndex
              ].map(({ value }) => value);
            } else {
              optionsOnStep = FORM_INFO[step as Steps][serviceCategory].map(
                ({ value }) => value
              );
            }

            // Add all those values as the step value
            return {
              stepValues: {
                ...state.stepValues,
                [step]: optionsOnStep,
              },
              stepValid: {
                ...state.stepValid,
                [step]: true,
              },
            };
          }

          // Toggled off: reset values for that step
          return {
            stepValues: {
              ...state.stepValues,
              [step]: [],
            },
            stepValid: {
              ...state.stepValid,
              [step]: false,
            },
          };
        });
      },
      addOtherValueOnStep: (step, value) => {
        set((state) => {
          // Let's format "other" values as "other:[value]"
          const otherValue = `other:${value}`;
          // Let's make a copy of the values of the step
          const currentStepValues = [...state.stepValues[step]];
          // Let's find the index if there are values
          // already that follows this format
          const otherValueIndex = currentStepValues.findIndex((v) =>
            v.includes("other:")
          );
          // If value is empty and there are no other values existing yet,
          // do nothing
          if (value === "" && otherValueIndex < 0) return state;
          // If value is empty, and there is already an other value existing
          // remove that other value and invalidate that step if
          // it's the only one selected
          if (value === "" && otherValueIndex >= 0) {
            currentStepValues.splice(otherValueIndex, 1);
            return {
              stepValues: {
                ...state.stepValues,
                [step]: currentStepValues,
              },
              stepValid: {
                ...state.stepValid,
                [step]:
                  currentStepValues.filter((v) => v !== "other").length > 0,
              },
            };
          }
          // If the value is not empty, and an other value is already existing,
          // change that value
          if (otherValueIndex >= 0) {
            currentStepValues[otherValueIndex] = otherValue;
            return {
              stepValues: {
                ...state.stepValues,
                [step]: currentStepValues,
              },
            };
          }
          // Last case: If the value is not empty and there are no other
          // values yet, simply append and validate
          return {
            stepValues: {
              ...state.stepValues,
              [step]: [...currentStepValues, otherValue],
            },
            stepValid: {
              ...state.stepValid,
              [step]: true,
            },
          };
        });
      },
      // This is used by generic checkbox options
      toggleCheckboxOnStep: (step, value) => {
        set((state) => {
          // Purge values on next steps
          get().purgeNextSteps(step);

          // Purge staff numbers
          if (step === 5 && state.serviceCategory === "medical-staffing") {
            get().changeCnaNum(0);
            get().changeMedTechNum(0);
            get().changeLvnNum(0);
            get().changeRnNum(0);
            get().changePtNum(0);
            get().changeOtNum(0);
            get().changeOfficeStaffNum(0);
            get().changePcNum(0);
          }

          // Copy stored array
          const storedArray = [...state.stepValues[step]];

          // Find value from array
          const valueInArray = storedArray.findIndex((v) => v === value);

          // Implement toggle: If not found add to array.
          if (valueInArray < 0) {
            const newStoredArray = [...state.stepValues[step], value];

            return {
              stepValues: {
                ...state.stepValues,
                [step]: newStoredArray,
              },
              stepValid: {
                ...state.stepValid,
                // Accept only 2 "other*" values or none, check if length is more than 0
                [step]:
                  newStoredArray.length > 0 &&
                  newStoredArray.filter((v) => v.startsWith("other")).length %
                    2 !==
                    1,
              },
            };
          }

          // If found, remove from array
          storedArray.splice(valueInArray, 1);

          // If value is "other", remove (other:[value]) if preset
          if (value === "other") {
            const otherValueIndex = storedArray.findIndex((v) =>
              v.includes("other:")
            );
            if (otherValueIndex >= 0) storedArray.splice(otherValueIndex, 1);
          }

          // Remove also a 'select-all' value from the array when present.
          const selectAllIndex = storedArray.findIndex(
            (value) => value === "select-all"
          );
          if (selectAllIndex >= 0) storedArray.splice(selectAllIndex, 1);

          return {
            stepValues: {
              ...state.stepValues,
              [step]: storedArray,
            },
            stepValid: {
              ...state.stepValid,
              // Accept only 2 "other*" values or none, check if length is more than 0
              [step]:
                storedArray.length > 0 &&
                storedArray.filter((v) => v.startsWith("other")).length % 2 !==
                  1,
            },
          };
        });
      },
      toggleRadioOnStep: (s: number, v: string) => {
        set((state) => {
          // Purge values on next steps
          get().purgeNextSteps(s);

          const newStoredArray = [v];
          return {
            stepValues: {
              ...state.stepValues,
              [s]: newStoredArray,
            },
            stepValid: {
              ...state.stepValid,
              // Accept only 2 "other*" values or none, check if length is more than 0
              [s]:
                newStoredArray.length > 0 &&
                newStoredArray.filter((v) => v.startsWith("other")).length %
                  2 !==
                  1,
            },
          };
        });
      },
      purgeNextSteps: (s: number) => {
        set((state) => {
          const newState = { ...state };
          for (const step of range(s + 1, 10)) {
            newState.stepValues[step] = [];
            newState.stepValid[step] = false;
          }
          return newState;
        });
      },
      isScheduling: false,
      setIsScheduling: (s: boolean) => {
        set(() => ({
          isScheduling: s,
        }));
      },
    }),
    {
      name: "assessment-basic-info",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        submittedStep1Values: state.submittedStep1Values,
        serviceCategory: state.serviceCategory,
        stepValues: state.stepValues,
        isScheduling: state.isScheduling,
      }),
    }
  )
);

// 😅 https://github.com/pmndrs/zustand/discussions/1553#discussioncomment-4726200
useAssessmentStore.setState({
  totalSteps:
    useAssessmentStore.getState().serviceCategory === "services-for-families"
      ? TOTAL_B2C_ASSESSMENT_STEPS
      : TOTAL_B2B_ASSESSMENT_STEPS,
});
